import React from 'react'
import { cn } from '@/utils/cn'
import { useTheme } from '@/hooks/useTheme'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ...props
  }, ref) => {
    const { isDark } = useTheme()
    const baseClasses = 'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 border border-transparent'

    const variantClasses = {
      primary: cn(
        'text-white focus:ring-primary-500/50',
        isDark
          ? 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800'
          : 'bg-primary-500 hover:bg-primary-600 active:bg-primary-700'
      ),
      secondary: cn(
        'text-white focus:ring-secondary-500/50',
        isDark
          ? 'bg-secondary-600 hover:bg-secondary-700 active:bg-secondary-800'
          : 'bg-secondary-500 hover:bg-secondary-600 active:bg-secondary-700'
      ),
      accent: cn(
        'text-white focus:ring-accent-500/50',
        isDark
          ? 'bg-accent-600 hover:bg-accent-700 active:bg-accent-800'
          : 'bg-accent-500 hover:bg-accent-600 active:bg-accent-700'
      ),
      ghost: cn(
        'bg-transparent focus:ring-secondary-500/20',
        isDark
          ? 'text-secondary-300 hover:bg-secondary-800 active:bg-secondary-700'
          : 'text-secondary-700 hover:bg-secondary-100 active:bg-secondary-200'
      ),
      outline: cn(
        'bg-transparent focus:ring-primary-500/50 hover:text-white',
        isDark
          ? 'border-primary-400 text-primary-400 hover:bg-primary-400'
          : 'border-primary-500 text-primary-500 hover:bg-primary-500 active:bg-primary-600'
      ),
    }

    const sizeClasses = {
      sm: 'text-sm px-3 py-1.5 min-h-[32px]',
      md: 'text-base px-4 py-2 min-h-[40px]',
      lg: 'text-lg px-6 py-3 min-h-[48px]',
      xl: 'text-xl px-8 py-4 min-h-[56px]',
    }

    // Loading spinner component
    const LoadingSpinner = () => (
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    )

    return (
      <button
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          (disabled || loading) && 'opacity-50 cursor-not-allowed pointer-events-none',
          className
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <LoadingSpinner />}
        {leftIcon && !loading && <span className="mr-2 flex-shrink-0">{leftIcon}</span>}
        {children}
        {rightIcon && !loading && <span className="ml-2 flex-shrink-0">{rightIcon}</span>}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }